<script setup>
import { computed, ref, onMounted, reactive } from 'vue';
import { useWidgets } from '@store/widgets';
import { Editable, Select, Button, Spin } from '@components';
import { useTranslation } from '../../composables/useTranslation';

const $widget = useWidgets();
const { t } = useTranslation();

const whatsapp = computed(() => $widget.currentWidget?.config?.whatsapp || {})
const icon = computed(() => $widget.currentWidget?.config?.icon || {})
const cta = computed(() => $widget.currentWidget?.config?.cta || {})
const form = computed(() => $widget.currentWidget?.config?.form || {})

const greetings = computed(() => $widget.currentWidget?.config?.greetings || {})
const simple = computed(() => $widget.currentWidget.config?.greetings?.templates?.simple || {})
const wave = computed(() => $widget.currentWidget.config?.greetings?.templates?.wave || {})

const device = ref($widget.isMobile ? 'phone' : 'desktop');

const iconPosition = computed(() => {
    if (icon.value.position !== 'custom') return {};

    return {
        top: icon.value.position_custom?.top ? icon.value.position_custom?.top + 'px !important' : 'auto',
        bottom: icon.value.position_custom?.bottom ? icon.value.position_custom?.bottom + 'px !important' : 'auto',
        left: icon.value.position_custom?.left ? icon.value.position_custom?.left + 'px !important' : 'auto',
        right: icon.value.position_custom?.right ? icon.value.position_custom?.right + 'px !important' : 'auto',
    };
})

const headingSizes = { small: '16px', medium: '18px', large: '20px' }
const textSizes = { small: '12px', medium: '14px', large: '16px' }
const iconSizes = { small: '45px', medium: '60px', large: '80px' }
const formSizes = { small: '280px', medium: '320px', large: '360px' }

const getSize = (size, size_custom, options = textSizes) => {
    return options[size] || size_custom + 'px';
}

const state = reactive({
    formLoaded: false,
})

// When to show form.
const showForm = ref(false);
const showGreetings = ref(true);
onMounted(() => {
    window.addEventListener('scroll', () => {
        const customize_form = document.getElementById('customize_form');
        if (customize_form) {
            showForm.value = customize_form.getBoundingClientRect().top < window.innerHeight - 500;
        }
    });

    // On click [data-name] elements.
    document.addEventListener('click', (e) => {
        if (e.target.dataset.name) {
            const targetElement = document.querySelector(`[name="${e.target.dataset.name}"]`)
            if (targetElement) {
                // Scroll to 100px above the element.
                targetElement.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });

                setTimeout(() => {
                    targetElement.focus();
                }, 500)
            }
        }
    })

});

// Greetings handle.
const hideGreetings = () => {
    showGreetings.value = false;

    setTimeout(() => {
        showGreetings.value = true;
    }, 2000)
}

const form_url = computed(() => {
    let id = form.value[form.value.mode + '_id'] || form.value.cf7_id;
    return `${formychat_admin_vars.site.url}/?formychat-form&form=${form.value.mode}&form_id=${id}&admin=true&header=${form.value.subtitle}&footer=${form.value.footer}&widget_id=${$widget.currentWidget.id}`;
})

const iFrame = ref(null)
const adjustHeight = (event) => {
    const iframe = event.target;
    
    if (!iframe) return;
    
    // Set initial styles
    iframe.style.transition = 'height 0.3s ease-in-out';
    iframe.style.maxHeight = '100%';
    iframe.style.height = '0px';
    iframe.style.maxHeight = 'calc(100vh - 180px)';
    
    // Wait for iframe content to be fully loaded
    iframe.addEventListener('load', () => {
        // Get the form element height
        const formElement = iframe.contentWindow.document.body.querySelector('.formychat-custom-form');
        if (!formElement) {
            state.formLoaded = true;  // Using external state object
            return;
        }
        
        const height = formElement.scrollHeight;
        const newHeight = height + 1;
        
        // Apply the new height with animation
        requestAnimationFrame(() => {
            iframe.style.height = `${newHeight}px`;
            iframe.style.maxHeight = `calc(100vh - 180px)`;
        });
        
        // Reset margin on html element if using CF7
        const htmlElement = iframe.contentWindow.document.querySelector('html');
        if (htmlElement) {
            htmlElement.style.margin = '0';
            htmlElement.style.padding = '0';
        }
    });
    
    // Optional: Handle resize events for responsive forms
    const resizeObserver = new ResizeObserver((entries) => {
        const formElement = iframe.contentWindow.document.body.querySelector('.formychat-custom-form');
        if (formElement) {
            const newHeight = formElement.scrollHeight + 1;
            iframe.style.height = `${newHeight}px`;
        }
        
        // Set formLoaded after animation using external state
        setTimeout(() => {
            state.formLoaded = true;
            resizeObserver.disconnect();
        }, 500);
    });
    
    // Start observing the form element for size changes
    const formElement = iframe.contentWindow.document.body.querySelector('.formychat-custom-form');
    if (formElement) {
        resizeObserver.observe(formElement);
    }
    
    // Cleanup function to disconnect observer when needed
    return () => {
        resizeObserver.disconnect();
    };
};

function mergeCustomDropdownIntoFields(baseFields, formConfig) {
    const cd = formConfig?.custom_dropdown
    if (!cd?.enabled || !Array.isArray(cd.options) || !cd.options.length) {
        return baseFields
    }
    const name = cd.name || 'dropdown'
    const selectField = {
        name,
        type: 'select',
        label: cd.label || '',
        default: cd.default_value || '',
        options: cd.options.map((o) => ({ value: o.value, label: o.label })),
        attributes: {
            required: !!cd.required,
            placeholder: cd.placeholder || '',
        },
    }
    const list = [...baseFields]
    const pos = cd.position || 'before_message'
    let insertAt = list.length
    if (pos === 'before_message') {
        const i = list.findIndex((f) => f.name === 'message')
        insertAt = i === -1 ? list.length : i
    } else if (pos === 'after_name') {
        const i = list.findIndex((f) => f.name === 'name')
        insertAt = i === -1 ? list.length : i + 1
    } else if (pos === 'after_email') {
        const i = list.findIndex((f) => f.name === 'email')
        insertAt = i === -1 ? list.length : i + 1
    } else if (pos === 'after_phone') {
        const i = list.findIndex((f) => f.name === 'phone')
        insertAt = i === -1 ? list.length : i + 1
    }
    list.splice(insertAt, 0, selectField)
    return list
}

// Form Fields (merge per-widget custom dropdown for FormyChat).
const fields = computed(() => {
    const base = formychat_admin_vars.data.form_fields || []
    if (form.value.mode !== 'formychat') {
        return base
    }
    return mergeCustomDropdownIntoFields(base, form.value)
})
</script>
<template>
    <div class="w-full max-w-[480px] flex-col gap-3 items-center z-20 relative">
        <!-- mobile save buttons  -->
        <div class="flex sm:hidden items-center mt-5 mb-10 w-full sm:w-fit gap-6 sm:justify-end sm:gap-2 z-20 bg-white"
            :class="{ 'justify-end': $widget.isTab('whatsapp'), 'justify-between': !$widget.isTab('whatsapp') }">
            <Button v-if="!$widget.isTab('whatsapp')" outline @click="$widget.prevTab()">{{ $widget.isTab('whatsapp') ?
                t('cancel') : '← ' + t('previous') }}</Button>
            <Button @click="$widget.isTab('triggers') ? $widget.saveWidget() : $widget.nextTab()">{{
                $widget.isTab('triggers') ? $widget.currentWidget.isNew ? '☁ ' + t('create') : '☁ ' + t('save') : t('next') + ' →' }}</Button>
        </div>
        <div class="w-full space-y-6"
            :class="{ 
                'md:sticky md:top-[7rem]': $widget.isTab('customize') || ($widget.isTab('whatsapp') && whatsapp.agent_mode === 'agents') || ($widget.isTab('greetings') && $widget.currentWidget.config.greetings.enabled),
                'z-0' : showForm
            }">
            <div class="border sm:min-h-[18rem] md:min-h-[20rem] xl:min-h-[22rem] border-slate-200 rounded shadow-sm mx-auto relative bg-gray-50"
                :class="{ 'w-phone': device === 'phone', 'w-desktop': device === 'desktop' }">
                <!-- header dots  -->
                <div
                    class="bg-slate-50 border-b border-slate-200 h-8 flex items-center gap-2 child:w-3 child:h-3 child:rounded-full px-3">
                    <span class="bg-red-400"></span>
                    <span class="bg-yellow-400"></span>
                    <span class="bg-green-400"></span>
                </div>
                <!-- content  -->
                <div class="absolute flex flex-col gap-3 w-fit scale-75"
                    :class="{ 
                        'bottom-2 right-2 items-end origin-bottom-right': icon.position === 'right', 
                        'bottom-2 left-2 items-start origin-bottom-left': icon.position === 'left',
                        'origin-bottom-center': icon.position === 'custom'
                        }"
                    :style="iconPosition">
                    <!-- form  -->
                    <transition name="form_slide">
                        <div v-if="(showForm && $widget.isTab('customize')) || ($widget.isTab('whatsapp') && whatsapp.agent_mode === 'agents')"
                            class="formychat-widget-form  max-h-[19rem] overflow-scroll transition-all" :style="{
                                fontFamily: form.font_family == 'sans-serif' ? '-apple-system, sans-serif' : `font-family: '${form.font_family}' !important`,
                                width: getSize(form.size, form.size_custom, formSizes),
                            }">
                            <!-- header   -->
                            <div class="formychat-widget-form-title"
                                :style="{ color: form.text_color, background: form.background_color }">
                                <span>
                                    <Editable v-model="form.title" :placeholder="t('contact_via_whatsapp')"
                                        data-name="form_title" />
                                </span>
                                <div>
                                    <svg xmlns="http://www.w3.org/2000/svg" @click="showForm = false"
                                        viewBox="0 0 16 16">
                                        <path fill-rule="evenodd"
                                            d="M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708" />
                                    </svg>
                                </div>
                            </div>
                            <!-- header  -->
                            <transition name="slide">
                                <div class="formychat-header px-5 pt-3" v-if="form.mode === 'formychat' && form.subtitle">
                                    <Editable v-model="form.subtitle" data-name="form_subtitle" />
                                </div>
                            </transition>
                            <!-- agents  -->
                            <div class="formychat-widget-agents"
                                v-if="whatsapp.agent_mode === 'agents' && $widget.isTab('whatsapp')">
                                <div v-if="whatsapp.agents.length" v-for="agent in whatsapp.agents"
                                    class="formychat-widget-agents-agent">
                                    <div class="formychat-widget-agents-agent-picture">
                                        <img :src="agent.avatar">
                                    </div>
                                    <div class="formychat-widget-agents-name">
                                        <div class="font-semibold text-[15px]">
                                            <Editable v-model="agent.name" data-name="agent_name" :placeholder="t('agent_name')" />
                                        </div>
                                        <div class="text-[12px] text-slate-400">
                                            <Editable v-model="agent.subtitle" data-name="agent_subtitle" :placeholder="t('agent_subtitle')" />
                                        </div>
                                    </div>
                                </div>
                                <div v-else
                                    class="bg-yellow-50 border border-yellow-200 px-6 py-4 rounded text-yellow-500 text-center">
                                    {{ t('create_your_first_agent') }}</div>
                            </div>
                            <!-- Form mode formychat  -->
                            <div v-if="form.mode === 'formychat' && (showForm && $widget.isTab('customize'))">
                                <!-- FORMYCHAT Content  -->
                                <div class="formychat-widget-form-fields" style="width: 100% !important;">
                                    <fieldset v-for="field in fields" :key="field.name">

                                        <!-- label  -->
                                        <label v-if="field.label" :for="field.name" class="formychat-widget-form-fields-label">
                                            {{ field.label }}
                                        </label>

                                        <!-- Text, Email, Password, Date, Number, Tel, Url, Color, Range, Search, Time, Week, Month, Image, File, Submit -->
                                        <input
                                        v-if="['text', 'email', 'password', 'date', 'number', 'tel', 'url', 'color', 'range', 'search', 'time', 'week', 'month', 'image', 'file', 'submit'].includes(field.type)"
                                        :type="field.type" class="formychat-widget-form-fields-input" 
                                        :placeholder="field.attributes.placeholder"
                                        :name="field.name"
                                        :required="field.attributes.required"
                                        :minlength="field.attributes.minlength"
                                        :maxlength="field.attributes.maxlength"
                                        :min="field.attributes.min"
                                        :max="field.attributes.max"
                                        :step="field.attributes.step"
                                        :pattern="field.attributes.pattern"
                                        />

                                        <!-- Textarea  -->
                                        <textarea class="formychat-widget-form-fields-input"
                                        v-if="field.type === 'textarea'"
                                        :name="field.name"
                                        :required="field.attributes.required"
                                        :minlength="field.attributes.minlength"
                                        :maxlength="field.attributes.maxlength"
                                        :placeholder="field.attributes.placeholder"
                                        />

                                        <!-- Phone with Country Code  -->
                                        <div v-if="form.show_country_code_field && field.type === 'phone'"
                                            class="w-full relative flex items-center ring-1 ring-gray-300 rounded gap-0 focus:ring-gray-600"
                                            tabindex="-1">
                                            <Select :options="$widget.contents.countries"
                                                class="border-0 ring-0 rounded bg-white px-1 gap-1 child:gap-1" white
                                                v-model="form.country_code" />
                                            <input type="text" tabindex="-1" :placeholder="t('enter_phone')"
                                                class="border-0 text-gray-400 ring-0 w-full pl-1 pr-2 placeholder:text-sm placeholder:text-gray-400"
                                                style="width: 100% !important;" />
                                        </div>

                                        <!-- Phone  -->
                                        <input v-if="!form.show_country_code_field && field.type === 'phone'" type="text"
                                            class="formychat-widget-form-fields-input"
                                            :name="field.name"
                                            :required="field.attributes.required"
                                            :minlength="field.attributes.minlength"
                                            :maxlength="field.attributes.maxlength"
                                            :placeholder="field.attributes.placeholder || t('enter_phone')"
                                        />

                                        <select v-if="field.type === 'select'" :name="field.name"
                                            :key="field.name + '-' + field.default"
                                            :required="field.attributes.required"
                                            class="formychat-widget-form-fields-input formychat-widget-form-select">
                                            <option v-if="field.attributes.placeholder" value=""
                                                :selected="!field.default">{{ field.attributes.placeholder }}</option>
                                            <option v-for="opt in (field.options || [])" :key="String(opt.value) + '-' + opt.label"
                                                :value="opt.value" :selected="opt.value === field.default">
                                                {{ opt.label }}
                                            </option>
                                        </select>

                                    </fieldset>
                                    
                                    <!-- footer  -->
                                    <transition name="slide">
                                        <div class="formychat-widget-form-footer" v-if="form.footer">
                                            <Editable v-model="form.footer" data-name="form_footer" />
                                        </div>
                                    </transition>
                                </div>
                                <div class="formychat-widget-form-submit">
                                    <button type="submit"
                                        :style="{ color: form.text_color, background: form.background_color }">
                                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16">
                                            <path
                                                d="M13.601 2.326A7.85 7.85 0 0 0 7.994 0C3.627 0 .068 3.558.064 7.926c0 1.399.366 2.76 1.057 3.965L0 16l4.204-1.102a7.9 7.9 0 0 0 3.79.965h.004c4.368 0 7.926-3.558 7.93-7.93A7.9 7.9 0 0 0 13.6 2.326zM7.994 14.521a6.6 6.6 0 0 1-3.356-.92l-.24-.144-2.494.654.666-2.433-.156-.251a6.56 6.56 0 0 1-1.007-3.505c0-3.626 2.957-6.584 6.591-6.584a6.56 6.56 0 0 1 4.66 1.931 6.56 6.56 0 0 1 1.928 4.66c-.004 3.639-2.961 6.592-6.592 6.592m3.615-4.934c-.197-.099-1.17-.578-1.353-.646-.182-.065-.315-.099-.445.099-.133.197-.513.646-.627.775-.114.133-.232.148-.43.05-.197-.1-.836-.308-1.592-.985-.59-.525-.985-1.175-1.103-1.372-.114-.198-.011-.304.088-.403.087-.088.197-.232.296-.346.1-.114.133-.198.198-.33.065-.134.034-.248-.015-.347-.05-.099-.445-1.076-.612-1.47-.16-.389-.323-.335-.445-.34-.114-.007-.247-.007-.38-.007a.73.73 0 0 0-.529.247c-.182.198-.691.677-.691 1.654s.71 1.916.81 2.049c.098.133 1.394 2.132 3.383 2.992.47.205.84.326 1.129.418.475.152.904.129 1.246.08.38-.058 1.171-.48 1.338-.943.164-.464.164-.86.114-.943-.049-.084-.182-.133-.38-.232" />
                                        </svg>
                                        <Editable v-model="form.submit" component="span" data-name="form_submit_text" />
                                    </button>
                                </div>
                            </div>
                            <!-- Form mode CF7  -->
                            <div v-else v-if="(showForm && $widget.isTab('customize'))"
                                :style="`--form-color: ${form.text_color}; --form-bg: ${form.background_color}; --form-font: ${form.font_family}`">
                                <!-- <div class="formychat-widget-custom-form preview h-full overflow-y-scroll" v-else v-html="$widget.currentWidget.form_html"></div> -->
                                <div class="formychat-widget-custom-form">
                                    <iframe 
                                    v-if="form_url" 
                                    @load="adjustHeight" 
                                    border="0" 
                                    width="100%" 
                                    :src="form_url"
                                    frameborder="0"
                                    javascript="disabled" 
                                    scrolling="no">
                                    </iframe>
                                </div>
                                <transition name="slide">
                                    <div class="formychat-footer mb-6 mt-0" v-if="form.mode === 'formychat' && form.footer">
                                        <Editable v-model="form.footer" data-name="form_footer" />
                                    </div>
                                </transition>
                            </div>
                        </div>
                    </transition>
                    <!-- greetings  -->
                    <transition name="bounce">
                        <div class="formychat-widget-greetings"
                            v-if="$widget.isTab('greetings') && greetings.enabled && showGreetings">
                            <!-- simple  -->
                            <div v-if="greetings.template === 'simple'" class="formychat-widget-greetings-simple"
                                :style="`background: ${simple.background_color} !important; ` + (simple.font_family == 'sans-serif' ? '-apple-system, sans-serif' : `font-family: '${simple.font_family}' !important;`)">
                                <!-- close  -->
                                <div @click="hideGreetings" class="formychat-widget-greetings-simple-close">
                                    <svg viewBox="0 0 6 6" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path
                                            d="M5.84006 0.909735C5.89007 0.861332 5.92996 0.803424 5.95742 0.739389C5.98487 0.675355 5.99934 0.606476 5.99998 0.536772C6.00061 0.467068 5.98741 0.397935 5.96112 0.333407C5.93484 0.268878 5.89601 0.210247 5.8469 0.160933C5.79779 0.11162 5.73937 0.0726119 5.67507 0.0461858C5.61077 0.0197597 5.54187 0.00644469 5.47238 0.00701731C5.40289 0.00758993 5.33422 0.0220389 5.27036 0.0495211C5.2065 0.0770034 5.14873 0.116969 5.10043 0.167085L3.00296 2.27044L0.906189 0.167085C0.858279 0.115507 0.800504 0.0741387 0.736311 0.0454462C0.672117 0.0167537 0.60282 0.00132536 0.532553 8.16984e-05C0.462287 -0.00116197 0.39249 0.0118046 0.327328 0.0382075C0.262165 0.0646105 0.202971 0.103909 0.153277 0.153759C0.103584 0.203609 0.0644082 0.262988 0.0380879 0.328355C0.0117676 0.393723 -0.00115833 0.463739 8.14426e-05 0.534226C0.00132121 0.604713 0.0167013 0.674228 0.0453039 0.738623C0.0739065 0.803018 0.115146 0.860975 0.166562 0.909036L2.26194 3.01309L0.165166 5.11645C0.0727267 5.21597 0.0224017 5.34759 0.0247938 5.4836C0.0271859 5.6196 0.0821082 5.74936 0.17799 5.84554C0.273871 5.94172 0.403226 5.99682 0.538802 5.99922C0.674377 6.00162 0.805589 5.95114 0.904793 5.85841L3.00296 3.75505L5.09974 5.85911C5.19894 5.95184 5.33015 6.00232 5.46573 5.99992C5.6013 5.99752 5.73066 5.94242 5.82654 5.84624C5.92242 5.75006 5.97734 5.6203 5.97974 5.4843C5.98213 5.34829 5.9318 5.21667 5.83936 5.11715L3.74399 3.01309L5.84006 0.909735Z"
                                            fill="#BDBDBD" />
                                    </svg>
                                </div>
                                <!-- heading  -->
                                <div class="formychat-widget-greetings-simple-heading"
                                    :style="{ color: simple.heading_color, fontSize: getSize(simple.heading_size, simple.heading_size_custom) }">
                                    <Editable v-model="simple.heading" data-name="simple_heading" />
                                </div>
                                <!-- message  -->
                                <div class="formychat-widget-greetings-simple-message"
                                    :style="{ color: simple.message_color, fontSize: getSize(simple.message_size, simple.message_size_custom) }">
                                    <Editable v-model="simple.message" />
                                </div>
                                <!-- shapes for style 2  -->
                                <div class="formychat-widget-greetings-simple-cloud" v-if="greetings.style === 2">
                                    <svg width="121" height="27" :style="{ color: simple.background_color }"
                                        viewBox="0 0 121 27" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path fill-rule="evenodd" clip-rule="evenodd"
                                            d="M120.167 26.9229H0.444336C6.30566 11.199 21.4561 0 39.2227 0C53.7012 0 66.4424 7.43732 73.835 18.7C79.0801 16.2946 84.9141 14.9537 91.0615 14.9537C102.41 14.9537 112.692 19.5233 120.167 26.9229Z"
                                            fill="currentColor" />
                                    </svg>
                                </div>
                            </div>
                            <!-- wave  -->
                            <div v-if="greetings.template === 'wave'"
                                :style="wave.font_family == 'sans-serif' ? '-apple-system, sans-serif' : `font-family: '${wave.font_family}' !important`">
                                <div v-if="greetings.style === 1"
                                    class="formychat-widget-greetings-wave formychat-widget-greetings-wave-1"
                                    :style="{ background: wave.background_color, color: wave.color, fontFamily: wave.font_family }">
                                    <!-- close  -->
                                    <div @click="hideGreetings" class="formychat-widget-greetings-wave-close">
                                        <svg viewBox="0 0 6 6" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path
                                                d="M5.84006 0.909735C5.89007 0.861332 5.92996 0.803424 5.95742 0.739389C5.98487 0.675355 5.99934 0.606476 5.99998 0.536772C6.00061 0.467068 5.98741 0.397935 5.96112 0.333407C5.93484 0.268878 5.89601 0.210247 5.8469 0.160933C5.79779 0.11162 5.73937 0.0726119 5.67507 0.0461858C5.61077 0.0197597 5.54187 0.00644469 5.47238 0.00701731C5.40289 0.00758993 5.33422 0.0220389 5.27036 0.0495211C5.2065 0.0770034 5.14873 0.116969 5.10043 0.167085L3.00296 2.27044L0.906189 0.167085C0.858279 0.115507 0.800504 0.0741387 0.736311 0.0454462C0.672117 0.0167537 0.60282 0.00132536 0.532553 8.16984e-05C0.462287 -0.00116197 0.39249 0.0118046 0.327328 0.0382075C0.262165 0.0646105 0.202971 0.103909 0.153277 0.153759C0.103584 0.203609 0.0644082 0.262988 0.0380879 0.328355C0.0117676 0.393723 -0.00115833 0.463739 8.14426e-05 0.534226C0.00132121 0.604713 0.0167013 0.674228 0.0453039 0.738623C0.0739065 0.803018 0.115146 0.860975 0.166562 0.909036L2.26194 3.01309L0.165166 5.11645C0.0727267 5.21597 0.0224017 5.34759 0.0247938 5.4836C0.0271859 5.6196 0.0821082 5.74936 0.17799 5.84554C0.273871 5.94172 0.403226 5.99682 0.538802 5.99922C0.674377 6.00162 0.805589 5.95114 0.904793 5.85841L3.00296 3.75505L5.09974 5.85911C5.19894 5.95184 5.33015 6.00232 5.46573 5.99992C5.6013 5.99752 5.73066 5.94242 5.82654 5.84624C5.92242 5.75006 5.97734 5.6203 5.97974 5.4843C5.98213 5.34829 5.9318 5.21667 5.83936 5.11715L3.74399 3.01309L5.84006 0.909735Z"
                                                fill="#BDBDBD" />
                                        </svg>
                                    </div>
                                    <!-- Main icon - Before heading  -->
                                    <div v-if="wave.show_icon && !['after_heading', 'after_message'].includes(wave.icon_position)"
                                        class="formychat-widget-greetings-wave-1-icon"> <img :src="wave.icon_url"
                                            class="w-full mx-auto" /> </div>
                                    <!-- heading  -->
                                    <h3 class="formychat-widget-greetings-wave-heading" x-show="wave.heading"
                                        :style="{ color: wave.heading_color, fontSize: getSize(wave.heading_size, wave.heading_size_custom) }">
                                        <Editable v-model="wave.heading" />
                                    </h3>
                                    <!-- Main icon - Before heading  -->
                                    <div v-if="wave.show_icon && wave.icon_position == 'after_heading'"
                                        class="formychat-widget-greetings-wave-1-icon"> <img :src="wave.icon_url"
                                            class="w-full mx-auto" /> </div>
                                    <!-- message  -->
                                    <div class="formychat-widget-greetings-wave-message"
                                        :style="{ color: wave.message_color, fontSize: getSize(wave.message_size, wave.message_size_custom) }">
                                        <Editable v-model="wave.message" />
                                    </div>
                                    <!-- Main icon - Before heading  -->
                                    <div v-if="wave.show_icon && wave.icon_position == 'after_message'"
                                        class="formychat-widget-greetings-wave-1-icon"> <img :src="wave.icon_url"
                                            class="w-full mx-auto" /> </div>
                                    <!-- CTA  -->
                                    <div v-if="wave.show_cta" class="formychat-widget-greetings-wave-1-cta"
                                        :style="{ background: wave.cta_background_color, color: wave.cta_text_color }">
                                        <div> <img :src="wave.cta_icon_url" class="w-full mx-auto" /> </div>
                                        <div v-if="wave.cta_text">
                                            <Editable v-model="wave.cta_text" />
                                        </div>
                                    </div>
                                </div>
                                <div v-if="greetings.style === 2"
                                    class="formychat-widget-greetings-wave formychat-widget-greetings-wave-2"
                                    :style="{ background: wave.background_color, fontFamily: wave.font_family }">
                                    <!-- close  -->
                                    <div @click="hideGreetings" class="formychat-widget-greetings-wave-close">
                                        <svg viewBox="0 0 6 6" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path
                                                d="M5.84006 0.909735C5.89007 0.861332 5.92996 0.803424 5.95742 0.739389C5.98487 0.675355 5.99934 0.606476 5.99998 0.536772C6.00061 0.467068 5.98741 0.397935 5.96112 0.333407C5.93484 0.268878 5.89601 0.210247 5.8469 0.160933C5.79779 0.11162 5.73937 0.0726119 5.67507 0.0461858C5.61077 0.0197597 5.54187 0.00644469 5.47238 0.00701731C5.40289 0.00758993 5.33422 0.0220389 5.27036 0.0495211C5.2065 0.0770034 5.14873 0.116969 5.10043 0.167085L3.00296 2.27044L0.906189 0.167085C0.858279 0.115507 0.800504 0.0741387 0.736311 0.0454462C0.672117 0.0167537 0.60282 0.00132536 0.532553 8.16984e-05C0.462287 -0.00116197 0.39249 0.0118046 0.327328 0.0382075C0.262165 0.0646105 0.202971 0.103909 0.153277 0.153759C0.103584 0.203609 0.0644082 0.262988 0.0380879 0.328355C0.0117676 0.393723 -0.00115833 0.463739 8.14426e-05 0.534226C0.00132121 0.604713 0.0167013 0.674228 0.0453039 0.738623C0.0739065 0.803018 0.115146 0.860975 0.166562 0.909036L2.26194 3.01309L0.165166 5.11645C0.0727267 5.21597 0.0224017 5.34759 0.0247938 5.4836C0.0271859 5.6196 0.0821082 5.74936 0.17799 5.84554C0.273871 5.94172 0.403226 5.99682 0.538802 5.99922C0.674377 6.00162 0.805589 5.95114 0.904793 5.85841L3.00296 3.75505L5.09974 5.85911C5.19894 5.95184 5.33015 6.00232 5.46573 5.99992C5.6013 5.99752 5.73066 5.94242 5.82654 5.84624C5.92242 5.75006 5.97734 5.6203 5.97974 5.4843C5.98213 5.34829 5.9318 5.21667 5.83936 5.11715L3.74399 3.01309L5.84006 0.909735Z"
                                                fill="#BDBDBD" />
                                        </svg>
                                    </div>
                                    <!-- Main icon - Before CTA  -->
                                    <div v-if="wave.show_icon && wave.icon_position !== 'after_cta'"
                                        class="formychat-widget-greetings-wave-2-icon"> <img :src="wave.icon_url"
                                            class="w-full mx-auto" /> </div>
                                    <!-- CTA  -->
                                    <div v-if="wave.show_cta" class="formychat-widget-greetings-wave-2-cta"
                                        :style="{ background: wave.cta_background_color }">
                                        <!-- heading  -->
                                        <h3 class="formychat-widget-greetings-wave-2-cta-heading"
                                            v-if="wave.cta_heading" x-text=""
                                            :style="{ color: wave.cta_heading_color, fontSize: getSize(wave.cta_heading_size, wave.cta_heading_size_custom) }">
                                            <Editable v-model="wave.cta_heading" />
                                        </h3>
                                        <!-- message  -->
                                        <div class="formychat-widget-greetings-wave-2-cta-message"
                                            v-if="wave.cta_message"
                                            :style="{ color: wave.cta_message_color, fontSize: getSize(wave.cta_message_size, wave.cta_message_size_custom) }">
                                            <Editable v-model="wave.cta_message" />
                                        </div>
                                    </div>
                                    <!-- Main icon - After CTA  -->
                                    <div v-if="wave.show_icon && wave.icon_position === 'after_cta'"
                                        class="formychat-widget-greetings-wave-2-icon"> <img :src="wave.icon_url"
                                            class="w-full mx-auto" /> </div>
                                </div>
                            </div>
                        </div>
                    </transition>
                    <!-- icon box  -->
                    <div class="flex items-center gap-2 cursor-pointer group transition-all duration-100"
                        :class="{ 'flex-row-reverse': icon.position === 'left', 'opacity-60 grayscale': !$widget.currentWidget.is_active }"
                        @click.prevent="showForm = !showForm">
                        <!-- cta  -->
                        <transition name="bounce">
                            <div v-if="cta.enabled"
                                class="shadow-lg drop-shadow-lg rounded-2xl px-4 py-2 transition-all duration-75"
                                :style="{ color: cta.color, background: cta.background_color, fontSize: getSize(cta.size, cta.size_custom, headingSizes) }">
                                <Editable v-model="cta.text" component="span" />
                            </div>
                        </transition>
                        <!-- icon  -->
                        <div class="rounded group-hover:opacity-95 overflow-hidden"
                            :style="{ width: getSize(icon.size, icon.size_custom, iconSizes), height: getSize(icon.size, icon.size_custom, iconSizes) }">
                            <img class="w-full h-auto" :src="icon.image_url || Image('whatsapp.svg')">
                        </div>
                    </div>
                </div>
            </div>
            <!-- handler  -->
            <div
                class="z-0 relative mx-auto text-slate-400 flex h-10 items-center justify-center  border-4 border-slate-200 bg-slate-200 child:h-full child:w-1/2 w-28 child:flex child:items-center child:justify-center cursor-pointer child:transition rounded overflow-hidden">
                <div @click.prevent="device = 'desktop'" :class="{ 'text-slate-700': device == 'desktop' }">
                    <svg xmlns="http://www.w3.org/2000/svg" class="fill-current w-5" viewBox="0 0 16 16">
                        <path
                            d="M6 12q0 1-.25 1.5H5a.5.5 0 0 0 0 1h6a.5.5 0 0 0 0-1h-.75Q10 13 10 12h4c2 0 2-2 2-2V4c0-2-2-2-2-2H2C0 2 0 4 0 4v6c0 2 2 2 2 2z" />
                    </svg>
                </div>
                <div @click.prevent="device = 'phone'" :class="{ 'text-slate-700': device == 'phone' }">
                    <svg xmlns="http://www.w3.org/2000/svg" class="fill-current w-5" viewBox="0 0 16 16">
                        <path
                            d="M3 2a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2zm6 11a1 1 0 1 0-2 0 1 1 0 0 0 2 0" />
                    </svg>
                </div>
                <div class="absolute rounded bg-white -z-10 left-0 top-0 transition duration-200"
                    :class="{ 'translate-x-full': device == 'phone' }"></div>
            </div>
        </div>
    </div>
</template>
<style scoped>
.w-phone {
    width: 260px;
    transition: width 0.2s;
}

@media screen and (max-width: 768px) {
    .w-phone {
        width: 100%;
    }
}

.w-desktop {
    width: 100%;
    transition: width 0.2s;
}
</style>